import { AxiosRequestConfig } from "axios";
import {
  DetailedUserProfile,
  LastCompletedWorkout,
  Subscription,
  User,
} from "queries/current-user/types";
import { setSentryUser } from "../lib/sentry-user";
import { apiClient } from "../constants";

/**
 * Validates session token format
 */
function isValidSessionToken(token: string | null): boolean {
  return token && 
         typeof token === 'string' && 
         token.length > 10 && 
         token.length < 2000 &&
         !token.includes(' ') && // Tokens shouldn't contain spaces
         token.trim() === token; // No leading/trailing whitespace
}

// Per-process cache + in-flight coalescing for /users/me. SSR pages
// (getServerSideProps in apps/web/pages/app/*) call this on every page nav
// — without caching, a user clicking around generates one Rocky call per
// click. 60s TTL is short enough to keep membership/role staleness
// bounded; in-flight Map collapses parallel cold-start bursts.
//
// Server-only: this module is bundled into the client too, but on the client
// we rely on react-query (`hooks.ts`) to handle caching. Putting a Map cache
// in a browser global creates cross-tab/cross-session staleness traps after
// logout. `IS_SERVER` gates all cache reads/writes so the client always hits
// the underlying fetch path.
//
// Note on `config` arg: cache keys are session-only, so two callers passing
// different `config` values (e.g. distinct request IDs) will share an entry.
// No current caller does this; callers needing fresh data should call
// `clearCachedUser(session)` first.
const USER_CACHE_TTL_MS = 60_000;
const USER_CACHE_MAX = 10_000;
const IS_SERVER = typeof window === "undefined";
type CachedUserEntry = { user: User; expiresAt: number };
const userCache = new Map<string, CachedUserEntry>();
const inFlightUserFetches = new Map<string, Promise<User>>();

function getCachedUser(session: string): User | undefined {
  const entry = userCache.get(session);
  if (!entry) return undefined;
  if (entry.expiresAt < Date.now()) {
    userCache.delete(session);
    return undefined;
  }
  return entry.user;
}

function setCachedUser(session: string, user: User): void {
  if (userCache.size >= USER_CACHE_MAX) {
    const firstKey = userCache.keys().next().value;
    if (firstKey) userCache.delete(firstKey);
  }
  userCache.set(session, {
    user,
    expiresAt: Date.now() + USER_CACHE_TTL_MS,
  });
}

/**
 * Invalidate the cached /users/me entry for a session token. Call this
 * immediately after a server-side mutation that changes user state (team
 * assignment, trial creation, role change) to ensure the next read reflects
 * the new state instead of returning the pre-mutation cached value.
 */
export function clearCachedUser(session: string | null | undefined): void {
  if (!session) return;
  userCache.delete(session);
  inFlightUserFetches.delete(session);
}

export const getCurrentUserFn = async (
  session: string | null,
  config: AxiosRequestConfig = {}
): Promise<User> => {
  const user = await resolveCurrentUser(session, config);
  // Every caller — client or per-request SSR scope — gets user context on its
  // own Sentry scope, including hits on THIS module's 60s cache and coalesced
  // in-flight fetches. Outer caches that wrap this function (e.g. web's
  // app/api/user-cache withCache layer) skip it entirely on their hits — those
  // paths set the user at their own request-scoped call sites instead.
  setSentryUser(user.id);
  return user;
};

const resolveCurrentUser = async (
  session: string | null,
  config: AxiosRequestConfig = {}
): Promise<User> => {
  if (IS_SERVER && session && isValidSessionToken(session)) {
    const cached = getCachedUser(session);
    if (cached) return cached;

    const inFlight = inFlightUserFetches.get(session);
    if (inFlight) return inFlight;

    const promise = doGetCurrentUserFn(session, config);
    inFlightUserFetches.set(session, promise);
    try {
      const user = await promise;
      setCachedUser(session, user);
      return user;
    } finally {
      inFlightUserFetches.delete(session);
    }
  }

  // Client side, or invalid/missing token: skip cache and hit the original
  // path. Invalid tokens still throw + report to Sentry as before.
  return doGetCurrentUserFn(session, config);
};

const doGetCurrentUserFn = async (
  session: string | null,
  config: AxiosRequestConfig = {}
): Promise<User> => {
  // Enhanced error handling for LADDER-WEBSITE-729
  try {
    // Validate session token before making request
    if (!isValidSessionToken(session)) {
      const error = new Error("Invalid session token format");
      // Log to Sentry for invalid tokens
      import("@sentry/nextjs").then(Sentry => {
        Sentry.captureException(error, {
          tags: {
            service: "rocky-api",
            endpoint: "/users/me",
            operation: "getCurrentUser",
            error_type: "validation_error"
          },
          extra: {
            hasSession: !!session,
            sessionType: typeof session,
            sessionLength: session?.length || 0,
            isString: typeof session === 'string',
            hasSpaces: session?.includes(' ') || false,
          },
          fingerprint: ['rocky-api-invalid-session-token']
        });
      }).catch(() => {});
      throw error;
    }

    const response = await apiClient.get<User>("/users/me", {
      headers: {
        Authorization: `Bearer ${session}`,
      },
      ...config,
    });

    return response.data;
  } catch (error: any) {
    // Enhanced error details for Sentry logging (LADDER-WEBSITE-729)
    const errorDetails = {
      status: error.response?.status,
      statusText: error.response?.statusText,
      errorMessage: error.message,
      errorCode: error.code,
      sessionTokenLength: session?.length || 0,
      hasSessionToken: !!session,
      sessionTokenValid: isValidSessionToken(session),
      baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
      endpoint: "/users/me",
      requestId: config.headers?.['x-request-id'] || 'unknown',
    };
    
    // Only capture 5xx errors and network errors in Sentry to avoid noise
    if (!error.response || error.response.status >= 500) {
      // Dynamic import to avoid SSR issues
      import("@sentry/nextjs").then(Sentry => {
        Sentry.captureException(error, {
          tags: {
            service: "rocky-api",
            endpoint: "/users/me",
            operation: "getCurrentUser",
            error_type: error.response ? "http_error" : "network_error"
          },
          extra: errorDetails,
          fingerprint: ['rocky-api-users-me-error', error.response?.status || 'network']
        });
      }).catch(() => {
        // Sentry import failed, continue without logging
      });
    }
    
    // Re-throw to maintain existing error handling flow
    throw error;
  }
};
export const getCurrentUser =
  (session: string | null, config: AxiosRequestConfig = {}) =>
  async (): Promise<User> =>
    getCurrentUserFn(session, config);

/**
 * Public user profile response from GET /users/{userID}
 * This endpoint is public and requires no authentication
 */
export interface PublicUserProfile {
  id: string;
  givenName: string | null;
  familyName: string | null;
  profileImg: string | null;
  role: string | null;
  lastActive: string | null;
  streakCount: number;
  bootcamp?: { id: string; name: string } | null;
  // Social handles (optional)
  tiktokHandle?: string;
  linkedinHandle?: string;
  twitterHandle?: string;
  instagramHandle?: string;
  snapchatHandle?: string;
  facebookHandle?: string;
  bioLocation?: string;
  bioDescription?: string;
  profileBackgroundImgixURL?: string;
}

/**
 * Get public user profile by ID - NO AUTHENTICATION REQUIRED
 * Uses the public GET /users/{userID} endpoint (legacyGetUser)
 *
 * Use this when you only need basic user info (name, profile image, role)
 * and don't have admin/coach privileges.
 */
export const getPublicUserInfo = async (
  userId: string
): Promise<PublicUserProfile | null> => {
  if (!userId) return null;

  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_API_BASE_URL}/users/${userId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      // Dynamic import to avoid SSR issues
      import("@sentry/nextjs")
        .then((Sentry) => {
          Sentry.captureMessage(`Failed to fetch public user info`, {
            level: "warning",
            tags: {
              service: "rocky-api",
              endpoint: "/users/{userId}",
              operation: "getPublicUserInfo",
            },
            extra: {
              userId,
              status: response.status,
              statusText: response.statusText,
            },
            fingerprint: ["rocky-api-public-user-error", String(response.status)],
          });
        })
        .catch(() => {});
      return null;
    }

    return response.json();
  } catch (error) {
    // Dynamic import to avoid SSR issues
    import("@sentry/nextjs")
      .then((Sentry) => {
        Sentry.captureException(error, {
          tags: {
            service: "rocky-api",
            endpoint: "/users/{userId}",
            operation: "getPublicUserInfo",
          },
          extra: { userId },
          fingerprint: ["rocky-api-public-user-network-error"],
        });
      })
      .catch(() => {});
    return null;
  }
};

export const getDetailedUserInfo = async (
  userId: string,
  session: string | null,
  _config: AxiosRequestConfig = {}
): Promise<DetailedUserProfile> => {
  if (!userId) throw new Error("User ID is required");
  if (!session) throw new Error("Session token is required");

  const graphqlEndpoint = process.env.NEXT_PUBLIC_API_BASE_URL_ROCKY_GRAPHQL;
  if (!graphqlEndpoint) {
    throw new Error("GraphQL endpoint not configured");
  }

  // GraphQL query for single user by ID
  const query = `
    query GetDetailedUser($userId: UUID!) {
      detailedUsers(filter: { id: { eq: $userId } }, first: 1) {
        edges {
          node {
            id
            createdTS
            givenName
            familyName
            email
            profileImgImgixURL
            gender
            dob
            roleName
            membershipStatus
            lastActivityTS
            trialExpirationTS
            isPushEnabled
            coachRoomID
            avgWorkoutRating
            totalWorkoutDurS
            numWorkoutsCompleted
            numWorkoutsCompleted7Days
            numWorkoutsCompleted14Days
            numWorkoutsCompleted30Days
            numWorkoutsIncomplete
            lastCompletedWorkoutID
            lastCompletedWorkoutWorkoutID
            lastCompletedWorkoutCreatedTS
            lastCompletedWorkoutCompletedTS
            lastCompletedWorkoutCompletionNumber
            lastCompletedWorkoutRating
            teamID
            numTeamJoins
            recentTeamHistory {
              teamID
              teamName
              joinedTS
            }
            utmType
            subscriptionStatus
            subscriptionCurrentPeriodEndTS
            subscriptionIsCancelingAtPeriodEnd
            subscriptionType
            subscriptionProductDurMonths
            subscriptionVendorName
            firstSubStartTS
          }
        }
      }
    }
  `;

  const response = await fetch(graphqlEndpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session}`,
    },
    body: JSON.stringify({
      query,
      variables: { userId },
    }),
  });

  if (!response.ok) {
    throw new Error(`GraphQL request failed: ${response.status}`);
  }

  const result = await response.json();

  if (result.errors?.length) {
    throw new Error(result.errors[0].message || "GraphQL query failed");
  }

  const edges = result.data?.detailedUsers?.edges;
  if (!edges || edges.length === 0) {
    throw new Error("User not found");
  }

  const user = edges[0].node;

  // Map GraphQL response to DetailedUserProfile shape
  return {
    id: user.id,
    createdAt: user.createdTS,
    givenName: user.givenName || "",
    familyName: user.familyName || "",
    email: user.email || "",
    profileImg: user.profileImgImgixURL || "",
    gender: user.gender || "",
    dob: user.dob || "",
    role: user.roleName || "",
    membershipStatus: user.membershipStatus || "",
    lastActivityAt: user.lastActivityTS || "",
    trialExpiration: user.trialExpirationTS || "",
    isPushEnabled: user.isPushEnabled || false,
    coachRoomId: user.coachRoomID || "",
    avgWorkoutRating: user.avgWorkoutRating || 0,
    totalWorkoutDurationS: user.totalWorkoutDurS || 0,
    numWorkoutsCompleted: user.numWorkoutsCompleted || 0,
    numWorkoutsCompleted7days: user.numWorkoutsCompleted7Days || 0,
    numWorkoutsCompleted14days: user.numWorkoutsCompleted14Days || 0,
    numWorkoutsCompleted30days: user.numWorkoutsCompleted30Days || 0,
    numWorkoutsIncomplete: user.numWorkoutsIncomplete || 0,
    lastCompletedWorkout: user.lastCompletedWorkoutID
      ? {
          id: user.lastCompletedWorkoutID,
          workoutId: user.lastCompletedWorkoutWorkoutID || "",
          createdAt: user.lastCompletedWorkoutCreatedTS || "",
          completedAt: user.lastCompletedWorkoutCompletedTS || "",
          completionNumber: user.lastCompletedWorkoutCompletionNumber || 0,
          rating: user.lastCompletedWorkoutRating || 0,
        }
      : null as unknown as LastCompletedWorkout,
    bootcampId: user.teamID || "",
    numBootcampJoins: user.numTeamJoins || 0,
    recentBootcampHistory: (user.recentTeamHistory || []).map(
      (t: { teamID: string; teamName: string; joinedTS: string }) => ({
        bootcampId: t.teamID,
        bootcampName: t.teamName,
        joinedAt: t.joinedTS,
      })
    ),
    utmType: user.utmType || "",
    subscription: user.subscriptionStatus
      ? {
          status: user.subscriptionStatus,
          renewalDate: user.subscriptionCurrentPeriodEndTS || "",
          cancelAtPeriodEnd: user.subscriptionIsCancelingAtPeriodEnd || false,
          offeringName: user.subscriptionType || "",
          offeringDurationInMonths: user.subscriptionProductDurMonths || 0,
          vendorName: user.subscriptionVendorName || "",
        }
      : null as unknown as Subscription,
    latestPaidPeriodStartedAt: user.firstSubStartTS || "",
  };
};
